import {
  Button,
  FormDateField,
  FormRoot,
  FormSelect,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useEffect } from "react";
import { z } from "zod";
import { useKpisByPositionId } from "@/modules/jd/presentation/api/queries";
import { config } from "@/shared/lib/config";
import { formatDateForInput, toISOForAPI } from "@/shared/lib/date-time";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import type {
  CreateKPIAssignmentInput,
  UpdateKPIAssignmentInput,
} from "../../domain/contracts";
import { useKPIQuery, useKPIsQuery } from "../api/queries";
import {
  getKpiLevelLabel,
  mapKpiLevelToAssignmentType,
} from "../utils/kpi-level-labels";

/** Calendar date string from API คงรูปแบบ; ISO เต็มรูปแบบให้ format ตาม local ของ browser */
function assignmentDateToInput(
  value: string | Date | undefined,
): string | undefined {
  if (value == null) return undefined;
  if (typeof value === "string") {
    const t = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  }
  return formatDateForInput(value);
}

type EmployeeItem = { id: string; name: string };
type PositionItem = { id: string; name: string };
type DepartmentItem = { id: string; name: string };

const AssignmentFormSchema = z
  .object({
    kpiId: z.string().min(1, "ຕ້ອງເລືອກ KPI"),
    assignmentType: z.enum(["employee", "position", "department"]),
    employeeId: z.string().optional(),
    positionId: z.string().optional(),
    departmentId: z.string().optional(),
    startDate: z.string().min(1, "ຕ້ອງເລືອກວັນທີເລີ່ມຕົ້ນ"),
    endDate: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.assignmentType === "employee") return !!data.employeeId;
      if (data.assignmentType === "position") return !!data.positionId;
      if (data.assignmentType === "department") return !!data.departmentId;
      return false;
    },
    {
      message: "ຕ້ອງເລືອກຜູ້ຮັບຜິດຊອບຕາມປະເພດການມອບໝາຍ",
    },
  )
  .refine(
    (data) => {
      if (data.endDate && data.startDate) {
        return data.endDate >= data.startDate;
      }
      return true;
    },
    {
      message: "ວັນທີສິ້ນສຸດຕ້ອງຫຼືຫຼາຍກວ່າວັນທີເລີ່ມຕົ້ນ",
    },
  );

const UpdateAssignmentFormSchema = z
  .object({
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    isActive: z.boolean().optional(),
  })
  .refine(
    (data) => {
      if (data.endDate && data.startDate) {
        return data.endDate >= data.startDate;
      }
      return true;
    },
    {
      message: "ວັນທີສິ້ນສຸດຕ້ອງຫຼືຫຼາຍກວ່າວັນທີເລີ່ມຕົ້ນ",
    },
  );

export type AssignmentFormValues = z.infer<typeof AssignmentFormSchema>;
export type UpdateAssignmentFormValues = z.infer<
  typeof UpdateAssignmentFormSchema
>;

interface KPIAssignmentFormProps {
  initialKpiId?: string;
  initialValues?: Partial<AssignmentFormValues>;
  updateInitialValues?: Partial<UpdateAssignmentFormValues>;
  onSubmit: (
    values: CreateKPIAssignmentInput | UpdateKPIAssignmentInput,
  ) => void;
  submitting?: boolean;
  isEdit?: boolean;
}

export function KPIAssignmentForm({
  initialKpiId,
  initialValues,
  updateInitialValues,
  onSubmit,
  submitting,
  isEdit = false,
}: KPIAssignmentFormProps) {
  const schema = isEdit ? UpdateAssignmentFormSchema : AssignmentFormSchema;
  const effectiveKpiId = initialKpiId || initialValues?.kpiId;
  const { data: selectedKPI } = useKPIQuery(effectiveKpiId || "");

  const methods = RHF.useForm<
    AssignmentFormValues | UpdateAssignmentFormValues
  >({
    resolver: zodResolver(schema) as never,
    defaultValues: isEdit
      ? {
          startDate: assignmentDateToInput(updateInitialValues?.startDate),
          endDate: assignmentDateToInput(updateInitialValues?.endDate),
          isActive: updateInitialValues?.isActive ?? true,
        }
      : {
          kpiId: effectiveKpiId || "",
          assignmentType:
            (initialValues?.assignmentType as
              | "employee"
              | "position"
              | "department"
              | undefined) ||
            (selectedKPI?.level
              ? mapKpiLevelToAssignmentType(selectedKPI.level)
              : undefined) ||
            "employee",
          employeeId: initialValues?.employeeId || "",
          positionId: initialValues?.positionId || "",
          departmentId: initialValues?.departmentId || "",
          startDate:
            assignmentDateToInput(initialValues?.startDate) ??
            formatDateForInput(new Date()),
          endDate: assignmentDateToInput(initialValues?.endDate),
        },
  });

  const assignmentType = methods.watch("assignmentType");
  const positionId = methods.watch("positionId");

  // Lock assignmentType to match KPI level when KPI is selected
  useEffect(() => {
    if (!selectedKPI?.level || !effectiveKpiId || isEdit) return;

    const mappedAssignmentType = mapKpiLevelToAssignmentType(
      selectedKPI.level,
    );
    methods.setValue("kpiId", effectiveKpiId);

    if (mappedAssignmentType !== assignmentType) {
      methods.setValue("assignmentType", mappedAssignmentType);
      methods.setValue("employeeId", "");
      methods.setValue("positionId", "");
      methods.setValue("departmentId", "");
    }
  }, [selectedKPI, assignmentType, effectiveKpiId, isEdit, methods]);

  const { data: jdKpisResponse } = useKpisByPositionId(
    assignmentType === "position" ? positionId : undefined,
  );
  const jdKpis = jdKpisResponse?.data ?? [];
  const { data: kpisData } = useKPIsQuery({ limit: 100 });

  const handleSubmit = (
    vals: AssignmentFormValues | UpdateAssignmentFormValues,
  ) => {
    if (isEdit) {
      const updateVals = vals as UpdateAssignmentFormValues;
      onSubmit({
        startDate: updateVals.startDate
          ? toISOForAPI(`${updateVals.startDate}T00:00:00`)
          : undefined,
        endDate: updateVals.endDate
          ? toISOForAPI(`${updateVals.endDate}T23:59:59.999`)
          : undefined,
        isActive: updateVals.isActive,
      });
    } else {
      const createVals = vals as AssignmentFormValues;
      const startISO = toISOForAPI(`${createVals.startDate}T00:00:00`);
      if (!startISO) {
        toast.error("ວັນທີເລີ່ມຕົ້ນບໍ່ຖືກຕ້ອງ");
        return;
      }
      onSubmit({
        kpiId: effectiveKpiId || createVals.kpiId,
        assignmentType: createVals.assignmentType,
        employeeId: createVals.employeeId || undefined,
        positionId: createVals.positionId || undefined,
        departmentId: createVals.departmentId || undefined,
        startDate: startISO,
        endDate: createVals.endDate
          ? toISOForAPI(`${createVals.endDate}T23:59:59.999`)
          : undefined,
      });
    }
  };

  return (
    <FormRoot<AssignmentFormValues | UpdateAssignmentFormValues>
      methods={methods}
      onSubmit={handleSubmit}
      className="space-y-4"
    >
      {!isEdit && (
        <>
          {!effectiveKpiId && (
            <FormSelect
              name="kpiId"
              label="KPI"
              requiredMark
              options={[
                ...(kpisData?.data?.map((kpi) => ({
                  value: kpi.id,
                  label: `${kpi.name} (${getKpiLevelLabel(kpi.level)})`,
                })) ?? []),
              ]}
            />
          )}

          {effectiveKpiId && selectedKPI && (
            <div>
              <label htmlFor="kpiId" className="mb-2 block font-medium text-sm">
                KPI
              </label>
              <div className="rounded-md border bg-muted p-2 text-sm">
                {selectedKPI.name} (ລະດັບ: {getKpiLevelLabel(selectedKPI.level)}))
              </div>
            </div>
          )}
          {/* 
          <RHF.Controller
            name="assignmentType"
            control={methods.control}
            render={({ field }) => (
              <div className="space-y-2">
                <Label className="font-medium text-sm">
                  ປະເພດການມອບໝາຍ <span className="text-destructive">*</span>
                </Label>
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={!!effectiveKpiId && !!selectedKPI}
                  className="flex flex-wrap gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="employee"
                      id="assignment-type-employee"
                    />
                    <Label
                      htmlFor="assignment-type-employee"
                      className="cursor-pointer font-normal text-sm"
                    >
                      ພະນັກງານ
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="position"
                      id="assignment-type-position"
                    />
                    <Label
                      htmlFor="assignment-type-position"
                      className="cursor-pointer font-normal text-sm"
                    >
                      ຕໍາແໜ່ງ
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="department"
                      id="assignment-type-department"
                    />
                    <Label
                      htmlFor="assignment-type-department"
                      className="cursor-pointer font-normal text-sm"
                    >
                      ພະແນກ
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}
          /> */}

          {assignmentType === "employee" && (
            <FormInfiniteCombobox<EmployeeItem>
              name="employeeId"
              label="ພະນັກງານ"
              requiredMark
              queryKey={["users-lookup"]}
              queryFn={(args) =>
                fetchLookupForInfinite(`${config.apiUrl}/users/lookup`, args)
              }
              preloadQueryFn={(id) =>
                hydrateLookupItem(`${config.apiUrl}/users/lookup`, id)
              }
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
              placeholder="ເລືອກພະນັກງານ..."
            />
          )}

          {assignmentType === "position" && (
            <>
              <FormInfiniteCombobox<PositionItem>
                name="positionId"
                label="ຕໍາແໜ່ງ"
                requiredMark
                queryKey={["positions-lookup"]}
                queryFn={(args) =>
                  fetchLookupForInfinite(
                    `${config.apiUrl}/positions/lookup`,
                    args,
                  )
                }
                preloadQueryFn={(id) =>
                  hydrateLookupItem(`${config.apiUrl}/positions/lookup`, id)
                }
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                placeholder="ເລືອກຕໍາແໜ່ງ..."
              />
              {positionId && jdKpis.length > 0 && (
                <div className="space-y-2">
                  <p className="font-medium text-muted-foreground text-sm">
                    KPI ທີ່ກຳນົດໃນ JD ສຳລັບຕໍາແໜ່ງນີ້ — ກົດເພື່ອເລືອກ
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {jdKpis.map((jk) => (
                      <Button
                        key={jk.id}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => methods.setValue("kpiId", jk.kpiId)}
                      >
                        {jk.kpiName ?? jk.kpiId}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {assignmentType === "department" && (
            <FormInfiniteCombobox<DepartmentItem>
              name="departmentId"
              label="ພະແນກ"
              requiredMark
              queryKey={["departments-lookup"]}
              queryFn={(args) =>
                fetchLookupForInfinite(
                  `${config.apiUrl}/departments/lookup`,
                  args,
                )
              }
              preloadQueryFn={(id) =>
                hydrateLookupItem(`${config.apiUrl}/departments/lookup`, id)
              }
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
              placeholder="ເລືອກພະແນກ..."
            />
          )}
        </>
      )}

      <FormDateField name="startDate" label="ວັນທີເລີ່ມຕົ້ນ" requiredMark={!isEdit} />

      <FormDateField name="endDate" label="ວັນທີສິ້ນສຸດ (ບໍ່ຈຳເປັນ)" />

      {isEdit && (
        <div>
          <label htmlFor="isActive" className="mb-2 block font-medium text-sm">
            ສະຖານະ
          </label>
          <RHF.Controller
            name="isActive"
            control={methods.control}
            render={({ field }) => (
              <SimpleSelect
                value={String(field.value ?? true)}
                onValueChange={(v) => field.onChange(v === "true")}
                options={[
                  { value: "true", label: "ກຳລັງໃຊ້ງານ" },
                  { value: "false", label: "ປິດການໃຊ້ງານ" },
                ]}
              />
            )}
          />
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4">
        <Button type="submit" disabled={submitting} isLoading={submitting}>
          {isEdit ? "ບັນທຶກ" : "ສ້າງ"}
        </Button>
      </div>
    </FormRoot>
  );
}
